import {
  Calendar,
  Home,
  Inbox,
  Search,
  Settings,
  MessagesSquare,
  Plus,
  User2,
  ChevronUp,
  ChevronsUpDown,
  PlusCircle,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Circle,
  CircleUser,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { NavLink, Link, useNavigate } from "react-router-dom";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

import { useUser } from "@/contexts/UserContext";
import { useDialog } from "@/contexts/DialogContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useUpsellDialog } from "../contexts/UpsellDialogContext";
import optionsIcon from "../assets/options-icon.png";
import { createPaymentUpdatePortalSessionFunction } from "../data/firebase";
import { useState } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import { needsPaymentUpdate } from "../data/access";
import { getConversationName } from "../data/helpers";

export function AppSidebar() {
  const { userData, loading } = useUser();
  const { openNewConversation } = useDialog();
  const { setOpenMobile, isMobile } = useSidebar();
  const { logout, access } = useAuth();
  const navigate = useNavigate();
  const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);
  const { openUpsell } = useUpsellDialog();
  const paymentFailed = needsPaymentUpdate(access);

  const handleOpenNewConversation = () => {
    openNewConversation();
    handleCloseSidebar();
  };

  const handleCloseSidebar = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Failed to sign out:", error);
    }
  };

  const conversations = userData?.conversations ?? [];

  return (
    <Sidebar className="bg-white shadow">
      <SidebarHeader>
        <Link to="/" className="p-2 flex items-center gap-2">
          <img src={optionsIcon} alt="Options AI" className="w-7 h-7" />
          <h3 className="text-lg font-semibold text-black">Options AI</h3>
        </Link>
        <SidebarGroup className="py-0">
          {!access.hasAccess && !paymentFailed && (
            <Button
              variant="custom"
              onClick={openUpsell}
              className="shadow mb-2 bg-gradient-to-br bg-purple-500 text-white font-bold hover:bg-purple-600"
            >
              Get Premium
            </Button>
          )}
          {paymentFailed && (
            <div className="mb-4 p-2 rounded-md bg-red-50 border">
              <div className="flex flex-col items-center gap-1">
                <AlertTriangle className="w-6 h-6 text-red-500" />
                <p className="text-sm font-bold text-center">
                  Payment Method Failed
                </p>
                <p className="text-xs text-gray-500 text-center">
                  Please update payment method to continue using Options AI
                </p>
              </div>
              <Button
                variant="destructive"
                className="w-full mt-2 h-9"
                onClick={async () => {
                  try {
                    setPaymentLinkLoading(true);
                    const { data } =
                      await createPaymentUpdatePortalSessionFunction({
                        returnUrl: window.location.href,
                      });

                    // Use type assertion to handle the response
                    const responseData = data as { url?: string };
                    if (responseData.url) {
                      window.location.href = responseData.url;
                    } else {
                      throw new Error("Invalid response from payment portal");
                    }
                  } catch (error) {
                    console.error(
                      "Failed to create payment update portal session:",
                      error
                    );
                    alert(
                      "Failed to create payment update portal session, please contact support"
                    );
                  } finally {
                    setTimeout(() => {
                      setPaymentLinkLoading(false);
                    }, 1000);
                  }
                }}
              >
                {paymentLinkLoading ? (
                  <Loader2 className="w-8 h-8 animate-spin" />
                ) : (
                  "Update Payment Method"
                )}
              </Button>
            </div>
          )}
          <Button
            variant="outline"
            onClick={handleOpenNewConversation}
            className="shadow-sm"
          >
            <Plus />
            New Conversation
          </Button>
        </SidebarGroup>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          {conversations.length > 0 ? (
            <SidebarGroupLabel>Conversations</SidebarGroupLabel>
          ) : (
            <SidebarGroupLabel>No Conversations Found</SidebarGroupLabel>
          )}

          {loading ? (
            <SidebarGroupContentSkeleton />
          ) : (
            <SidebarGroupContent>
              <SidebarMenu>
                {[...conversations].reverse().map((conversation) => (
                  <SidebarMenuItem key={conversation.id}>
                    <NavLink
                      to={`/c/${conversation.id}`}
                      onClick={handleCloseSidebar}
                    >
                      {({ isActive }) => (
                        <SidebarMenuButton
                          className={`
                            text-foreground no-underline
                          transition-colors duration-200
                          ${
                            isActive
                              ? "bg-gray-200 hover:bg-gray-200"
                              : "hover:bg-gray-200"
                          }
                        `}
                          // isActive={isActive}
                        >
                          <MessagesSquare />
                          <span>{getConversationName(conversation)}</span>
                        </SidebarMenuButton>
                      )}
                    </NavLink>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          )}
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton className="py-6 border">
                  {/* <Avatar className="w-6 h-6">
                    <AvatarFallback>
                      {userData?.name
                        ? userData?.name.charAt(0)
                        : userData?.email.charAt(0)}
                    </AvatarFallback>
                  </Avatar> */}
                  <CircleUser className=" !size-6" />
                  <p className="text-sm truncate flex-1">
                    {userData?.name ? userData?.name : userData?.email}
                  </p>
                  <ChevronsUpDown className="ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                className="w-[--radix-popper-anchor-width]"
              >
                <DropdownMenuItem
                  onClick={() => {
                    navigate("/account");
                    handleCloseSidebar();
                  }}
                >
                  <span>My Account</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    navigate("/support");
                    handleCloseSidebar();
                  }}
                >
                  <span>Support</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSignOut}>
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

function SidebarGroupContentSkeleton() {
  return (
    <SidebarGroupContent>
      <SidebarMenu>
        {[...Array(5)].map((_, i) => (
          <SidebarMenuItem key={i}>
            <SidebarMenuButton>
              {/* <Skeleton className="h-4 w-4" /> */}
              <Skeleton className="h-6 w-full" />
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroupContent>
  );
}
